/**
 * Create Chat Form - Single Page Version
 * Consolidates all chat creation steps into one comprehensive form
 */

import type {
  BuyerUserSelectOption,
  CreateChatFormData,
  SupplierUserSelectOption,
} from '@choco-admin/types';
import React from 'react';
import { FormProvider } from 'react-hook-form';

import { FormFooter } from '../../../../../components/FormFooter';
import { PageLoading } from '../../../../../components/PageLoading';
import { useCreateChatForm } from '../../../hooks/useCreateChatForm';
import { useDuplicateChatData } from '../../../hooks/useDuplicateChatData';

import { BuyerSelectionCard } from './steps/BuyerSelectionCard';
import { ChatSettingsStep } from './steps/ChatSettingsStep';
import { SupplierSelectionCard } from './steps/SupplierSelectionCard';

interface CreateChatFormProps {
  onCancel?: () => void;
}

/**
 * Stable identity for "no prefill". A fresh `{}` per render made every effect
 * keyed on this object run on every render — with one of them calling setValue,
 * which re-rendered, which built another `{}`. That loop ran until React bailed
 * out with "Maximum update depth exceeded", and it lasted as long as the
 * duplicate source chat took to load.
 */
const NO_INITIAL_VALUES: Partial<CreateChatFormData> = {};

export const CreateChatForm: React.FC<CreateChatFormProps> = ({ onCancel }) => {
  const { duplicateData, isLoading: isDuplicateLoading } =
    useDuplicateChatData();

  const formMethods = useCreateChatForm(duplicateData ?? NO_INITIAL_VALUES);
  const {
    formState: { isSubmitting },
    isLoading,
    onSubmit,
  } = formMethods;

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = () => {
    onSubmit();
  };

  // Show loading if duplicate data is being fetched
  if (isDuplicateLoading) {
    return <PageLoading text="Loading duplicate data..." />;
  }

  // Wait for duplicate data to be loaded before initializing the form
  const hasDuplicateParams = new URLSearchParams(window.location.search).has(
    'duplicateFrom'
  );
  if (duplicateData === null && hasDuplicateParams) {
    return <PageLoading text="Preparing duplicate data..." />;
  }

  return (
    <FormProvider {...formMethods}>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="title-large text-foreground">
            {hasDuplicateParams ? 'Create Duplicate Chat' : 'Create New Chat'}
          </h2>
          <p className="text-sm text-default-600 mt-2">
            {hasDuplicateParams
              ? 'Creating a new chat based on existing chat data'
              : 'Set up a new chat between buyer and distributor'}
          </p>
        </div>

        {/* Participants Section */}
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-foreground">
              Select Participants
            </h3>
            <p className="text-sm text-default-600 mt-1">
              Choose the buyer and distributor for this chat
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BuyerSelectionCard
              onBuyerSelect={(buyer) => {
                formMethods.setValue('buyer', buyer, { shouldValidate: true });
              }}
              onBuyerUserToggle={(user) => {
                const currentUsers = formMethods.getValues('buyerUsers') || [];
                const userExists = currentUsers.some(
                  (u: BuyerUserSelectOption) => u.value === user.value
                );

                if (userExists) {
                  const filteredUsers = currentUsers.filter(
                    (u: BuyerUserSelectOption) => u.value !== user.value
                  );
                  formMethods.setValue('buyerUsers', filteredUsers, {
                    shouldValidate: true,
                  });
                } else {
                  formMethods.setValue('buyerUsers', [...currentUsers, user], {
                    shouldValidate: true,
                  });
                }
              }}
              onRemoveBuyerUser={(userId) => {
                const currentUsers = formMethods.getValues('buyerUsers') || [];
                const filteredUsers = currentUsers.filter(
                  (u: BuyerUserSelectOption) => u.value !== userId
                );
                formMethods.setValue('buyerUsers', filteredUsers, {
                  shouldValidate: true,
                });
              }}
            />

            <SupplierSelectionCard
              onSupplierSelect={(supplier) => {
                formMethods.setValue('supplier', supplier, {
                  shouldValidate: true,
                });
              }}
              onSupplierUserToggle={(user) => {
                const currentUsers =
                  formMethods.getValues('supplierUsers') || [];
                const userExists = currentUsers.some(
                  (u: SupplierUserSelectOption) => u.value === user.value
                );

                if (userExists) {
                  const filteredUsers = currentUsers.filter(
                    (u: SupplierUserSelectOption) => u.value !== user.value
                  );
                  formMethods.setValue('supplierUsers', filteredUsers);
                } else {
                  formMethods.setValue('supplierUsers', [
                    ...currentUsers,
                    user,
                  ]);
                }
              }}
              onRemoveSupplierUser={(userId) => {
                const currentUsers =
                  formMethods.getValues('supplierUsers') || [];
                const filteredUsers = currentUsers.filter(
                  (u: SupplierUserSelectOption) => u.value !== userId
                );
                formMethods.setValue('supplierUsers', filteredUsers);
              }}
            />
          </div>
        </div>

        {/* Chat Settings Section */}
        <div className="space-y-6">
          <ChatSettingsStep />
        </div>

        {/* Form Footer */}
        {/* The Create button is not gated on `isValid`: clicking it always runs
            the resolver, which renders field-level errors and blocks the
            `chatCreate` call when the form is invalid (legacy parity). It is only
            disabled while a submission is in flight. */}
        <FormFooter
          saveText="Create Chat"
          cancelText="Cancel"
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSubmitting || isLoading}
          isSaveDisabled={isSubmitting || isLoading}
          isCancelDisabled={isSubmitting || isLoading}
        />
      </div>
    </FormProvider>
  );
};
